import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, LogIn, LogOut, Clock, Wrench } from "lucide-react";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

export const WsReceive = ({ Pick, token, worker, setWorker, onDone }) => {
  const [vn, setVn] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!vn) return toast.error("اختر المركبة");
    if (!reason.trim()) return toast.error("اكتب سبب دخول المركبة");
    setBusy(true);
    try {
      localStorage.setItem("ws_worker", worker);
      const r = await api.workshopReceive(token, { vehicle_number: vn, reason: reason.trim(), worker });
      toast.success(r.message); setVn(""); setReason(""); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الاستلام"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="ws-receive">
      <p className="rounded-xl bg-blue-50 p-3 text-xs text-blue-800">عند استلام المركبة تتحول حالتها تلقائياً إلى «الورشة» وتُستلم من الموظف، وتعود للخدمة عند تسجيل الصيانة أو التسليم.</p>
      <section className="space-y-2"><div className="text-sm font-bold">1. المركبة</div><Pick token={token} value={vn} onChange={setVn} custodyOnly /></section>
      <section className="space-y-2"><div className="text-sm font-bold">2. سبب الدخول</div>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="مثال: صوت في المحرك، تغيير فحمات…" data-testid="ws-receive-reason" /></section>
      <div><div className="mb-1 text-xs text-slate-500">اسم الفني / المسؤول (اختياري)</div><Input value={worker} onChange={(e) => setWorker(e.target.value)} data-testid="ws-receive-worker" /></div>
      <Button onClick={go} disabled={busy} className="h-12 w-full gap-2 text-base" data-testid="ws-receive-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />} استلام المركبة للورشة</Button>
    </div>
  );
};

export const WsOrders = ({ token, worker, onLog, onCount }) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [note, setNote] = useState("");
  const load = useCallback(() => api.workshopOrders(token).then((r) => { setRows(r); onCount?.(r.length); }).catch(() => setRows([])), [token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  const release = async (o) => {
    setBusy(o.id);
    try { const r = await api.workshopRelease(token, { order_id: o.id, note, worker }); toast.success(r.message); setConfirm(null); setNote(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التسليم"); } finally { setBusy(""); }
  };
  if (!rows) return <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div>;
  if (!rows.length) return <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-400" data-testid="ws-orders-empty"><Wrench className="mx-auto mb-2 h-8 w-8" />لا توجد مركبات في الورشة حالياً</div>;
  return (
    <div className="space-y-3" data-testid="ws-orders">
      {rows.map((o) => (
        <div key={o.id} className="rounded-2xl border border-orange-200 bg-white p-4" data-testid={`ws-order-${o.id}`}>
          <div className="flex items-center justify-between">
            <p className="font-heading text-lg font-bold" dir="ltr">{o.vehicle_number}</p>
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${o.days >= 7 ? "bg-red-100 text-red-700" : "bg-orange-100 text-orange-700"}`}><Clock className="h-3 w-3" />{o.days} يوم</span>
          </div>
          <p className="mt-1 text-sm text-slate-600">{o.reason || "—"}</p>
          <p className="mt-1 text-[11px] text-slate-400">دخلت {o.opened_date} · {o.opened_by}{o.returned_from ? ` · استُلمت من ${o.returned_from}` : ""}</p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={() => onLog(o.vehicle_number)} className="gap-1" data-testid={`ws-order-log-${o.id}`}><Wrench className="h-4 w-4" /> تسجيل الصيانة</Button>
            <Button onClick={() => { setConfirm(o.id); setNote(""); }} className="gap-1 bg-emerald-600 hover:bg-emerald-700" data-testid={`ws-order-release-${o.id}`}>
              <LogOut className="h-4 w-4" /> تسليم / خروج</Button>
          </div>
          {confirm === o.id && (
            <div className="mt-3 space-y-2 rounded-xl bg-emerald-50 p-3" data-testid={`ws-order-confirm-${o.id}`}>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="ملاحظة التسليم (اختياري)" data-testid={`ws-order-note-${o.id}`} />
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={() => setConfirm(null)}>إلغاء</Button>
                <Button onClick={() => release(o)} disabled={busy === o.id} className="gap-1 bg-emerald-600 hover:bg-emerald-700" data-testid={`ws-order-release-confirm-${o.id}`}>
                  {busy === o.id && <Loader2 className="h-4 w-4 animate-spin" />} تأكيد الخروج</Button>
              </div>
            </div>)}
        </div>))}
    </div>
  );
};
